import type { GeometryElement } from 'jsxgraph';
import {
  boundingBoxToTuple,
  createElementFactory,
  JsxgraphBaseWidget,
  renderLatex,
  type BoundLabel,
  type JsxgraphStatic,
  type JsxgraphThemeService,
  type WidgetState,
} from '@examdojo/core/widget';
import type { ThemeColorWithShade } from '@examdojo/core/widget';
import {
  computeTrigLineEndpoints,
  findNearestSpecialAngle,
  findSpecialAngleIndex,
  getAxisMarkerData,
  getSignInfo,
  getSpecialAngleAngleLabel,
  getSpecialAngleCoordLabel,
  getSpecialAngleLabelPosition,
  getSpecialAngles,
  getTrigLineLabelPosition,
  normalizeAngle,
  parseAngle,
  snapToNearestSpecialAngle,
} from './unit-circle.math';
import type {
  PointConfig,
  QuadrantsConfig,
  SpecialAnglesConfig,
  TrigLine,
  TrigLinesConfig,
  UnitCircleWidgetConfig,
  ValueLabelConfig,
} from './unit-circle.model';

/** Default trig line colors (theme color tokens). */
const TRIG_LINE_COLORS: Record<TrigLine, ThemeColorWithShade> = {
  sin: 'purple-500',
  cos: 'red-500',
  tan: 'amber-500',
  cot: 'green-500',
  sec: 'orange-500',
  csc: 'pink-500',
};

const ALL_TRIG_LINES: TrigLine[] = ['sin', 'cos', 'tan', 'sec', 'csc', 'cot'];

/** JSXGraph layer for the interactive glider (above default elements). */
const GLIDER_LAYER = 9;

type ElementFactoryType = ReturnType<typeof createElementFactory>;

export class UnitCircleWidget extends JsxgraphBaseWidget<UnitCircleWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `unit-circle-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  readonly id: string;
  readonly type = 'unit-circle';

  private config?: UnitCircleWidgetConfig;
  private currentAngle = 0;
  private gliderElement?: GeometryElement;

  // Dynamic elements (updated on drag)
  private readonly trigLineElements: Map<
    TrigLine,
    { line: GeometryElement; a: GeometryElement; b: GeometryElement; lineLabel?: BoundLabel }
  > = new Map();
  private refTriangleVertices: GeometryElement[] = [];
  private projectionElements: GeometryElement[] = [];
  private coordLabel?: BoundLabel;
  private traceWaveCurve?: GeometryElement;

  protected getCurrentConfig(): UnitCircleWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    return {
      type: this.type,
      id: this.id,
      angle: this.currentAngle,
    };
  }

  init(config: UnitCircleWidgetConfig): void {
    this.config = config;
    const { params, display } = config;

    const radius = params.radius ?? 1;
    const center: [number, number] = params.center ?? [0, 0];
    const angleUnit = params.angleUnit ?? 'rad';

    // Resolve element-based configs
    const pointConfig = this.resolvePointConfig(params.point);
    const trigLinesConfig = this.resolveTrigLinesConfig(params.trigLines);
    const specialAnglesConfig = this.resolveSpecialAnglesConfig(params.specialAngles);
    const quadrantsConfig = this.resolveQuadrantsConfig(params.quadrants);

    const hasPoint = pointConfig !== null;
    if (hasPoint) {
      this.currentAngle = parseAngle(pointConfig.angle ?? Math.PI / 4);
    }

    // 1. Board setup — fixedAxis: 'x' ensures equal units (circles render as circles)
    const coordinateSystem = display?.coordinateSystem;
    const defaultPadding = radius + 1.5;
    const defaultBbox = {
      xStart: center[0] - defaultPadding,
      xEnd: center[0] + defaultPadding + (params.traceWave ? 2 * Math.PI + 1 : 0),
      yStart: center[1] - defaultPadding,
      yEnd: center[1] + defaultPadding,
    };
    let boundingbox = boundingBoxToTuple(coordinateSystem?.boundingbox ?? defaultBbox);

    const fixedAxis = coordinateSystem?.fixedAxis ?? 'x';
    if (fixedAxis) {
      boundingbox = this.adjustBboxForFixedAxis(boundingbox, fixedAxis);
    }

    this.initBoard({ boundingbox, axis: false });

    if (fixedAxis) {
      this.setupFixedAxisObserver();
    }

    // Default axes — use pi/degree format on x-axis when tracing a wave, standard numeric otherwise
    const defaultAxes = this.buildDefaultAxes(params.traceWave, angleUnit);
    const defaultCoordSystem: typeof coordinateSystem = {
      ...coordinateSystem,
      axes: coordinateSystem?.axes ?? { custom: defaultAxes },
    };
    this.createCoordinateSystem(defaultCoordSystem);

    // 2. Element factory
    const elementFactory = createElementFactory(this.board, this.container, this.themeService, {
      defaultPrecision: display?.math?.defaultPrecision,
    });

    // 3. Create the circle
    const circleResult = elementFactory.circle({
      type: 'centerRadius',
      center,
      radius,
      style: display?.circle ?? { strokeColor: 'neutral-700', fillOpacity: 0 },
      centerStyle: display?.centerPoint ?? { size: 2, color: 'neutral-500', visible: true, fixed: true },
    });
    this.objects.set('circle', circleResult.circle);
    this.objects.set('center', circleResult.center);

    // 4. Axis markers ((1,0) / π/2 / 0° variants)
    if (params.axisMarkers && params.axisMarkers !== 'none') {
      this.createAxisMarkers(params.axisMarkers, radius, center, elementFactory);
    }

    // 5. Special angles (before glider so glider renders on top)
    if (specialAnglesConfig) {
      this.createSpecialAngles(radius, center, specialAnglesConfig, angleUnit, display, elementFactory);
    }

    // 6. Quadrants
    if (quadrantsConfig) {
      this.createQuadrantInfo(radius, center, quadrantsConfig, hasPoint);
    }

    // 7. Trig lines (before glider for layering)
    if (hasPoint && trigLinesConfig) {
      this.createTrigLines(trigLinesConfig.lines ?? ALL_TRIG_LINES, radius, center, display, elementFactory);

      if (trigLinesConfig.referenceTriangle) {
        this.createReferenceTriangle(radius, center, display, elementFactory);
      }
    }

    // 8. Draggable point on circle (glider) — created after other elements so it's on top
    if (hasPoint) {
      const showCoordLabel = pointConfig.coordinateLabel !== false;

      const gliderResult = elementFactory.glider({
        coords: [center[0] + radius * Math.cos(this.currentAngle), center[1] + radius * Math.sin(this.currentAngle)],
        slideObject: circleResult.circle,
        name: showCoordLabel ? this.getCoordLabelText(radius) : undefined,
        style: {
          color: 'sky-500',
          size: 5,
          fixed: false,
          ...(showCoordLabel
            ? {
                label: {
                  text: this.getCoordLabelText(radius),
                  position: 'topRight' as const,
                  visible: true,
                  variant: 'secondary' as const,
                },
              }
            : {}),
          ...display?.point,
        },
        onDrag: (x, y) => {
          const rawAngle = normalizeAngle(Math.atan2(y - center[1], x - center[0]));
          const snapMode = pointConfig.snap === true ? 'attract' : pointConfig.snap;

          if (snapMode === 'snap') {
            // Always jump to the nearest special angle
            const nearest = findNearestSpecialAngle(rawAngle);
            this.currentAngle = nearest;
            const sx = center[0] + radius * Math.cos(nearest);
            const sy = center[1] + radius * Math.sin(nearest);
            (
              this.gliderElement as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void }
            ).setPositionDirectly(1, [sx, sy]);
          } else if (snapMode === 'attract') {
            // Snap when near a special angle, free movement otherwise
            const snapped = snapToNearestSpecialAngle(rawAngle);
            if (snapped !== null) {
              this.currentAngle = snapped;
              const sx = center[0] + radius * Math.cos(snapped);
              const sy = center[1] + radius * Math.sin(snapped);
              (
                this.gliderElement as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void }
              ).setPositionDirectly(1, [sx, sy]);
            } else {
              this.currentAngle = rawAngle;
            }
          } else {
            this.currentAngle = rawAngle;
          }

          this.updateDynamicElements();
        },
      });
      this.gliderElement = gliderResult.glider;
      this.objects.set('glider', gliderResult.glider);

      // Bring glider to front layer
      (gliderResult.glider as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute({
        layer: GLIDER_LAYER,
      });

      if (gliderResult.label) {
        this.coordLabel = gliderResult.label;
      }

      if (pointConfig.interactive === false) {
        (gliderResult.glider as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute({
          fixed: true,
        });
      }

      // Radius line from center to glider
      const radiusResult = elementFactory.line({
        a: circleResult.center,
        b: gliderResult.glider,
        style: display?.radiusLine ?? { color: 'sky-500', strokeWidth: 1.5 },
      });
      this.objects.set('radiusLine', radiusResult.line);

      // Axis projections
      if (pointConfig.axisProjections) {
        this.createAxisProjections(radius, center, display, elementFactory);
      }

      // Value labels at canvas edges
      if (pointConfig.valueLabels) {
        const vlConfigs: ValueLabelConfig[] =
          pointConfig.valueLabels === true
            ? [
                { value: 'cos', position: 'canvasBottom' },
                { value: 'sin', position: 'canvasLeft' },
              ]
            : pointConfig.valueLabels;
        this.createValueLabels(vlConfigs, radius, center);
      }
    }

    // 9. Trace wave
    if (hasPoint && params.traceWave) {
      this.createTraceWave(params.traceWave, radius, center, display, elementFactory);
    }
  }

  override destroy(): void {
    this.trigLineElements.clear();
    this.refTriangleVertices = [];
    this.projectionElements = [];
    this.coordLabel = undefined;
    this.traceWaveCurve = undefined;
    this.gliderElement = undefined;
    super.destroy();
  }

  // ===========================================================================
  // CONFIG RESOLVERS
  // ===========================================================================

  private resolvePointConfig(input: boolean | PointConfig | undefined): PointConfig | null {
    if (!input) {
      return null;
    }
    if (input === true) {
      return {};
    }
    return input;
  }

  private resolveTrigLinesConfig(input: boolean | TrigLine[] | TrigLinesConfig | undefined): TrigLinesConfig | null {
    if (!input) {
      return null;
    }
    if (input === true) {
      return { lines: ALL_TRIG_LINES };
    }
    if (Array.isArray(input)) {
      return { lines: input };
    }
    return input;
  }

  private resolveSpecialAnglesConfig(input: boolean | SpecialAnglesConfig | undefined): SpecialAnglesConfig | null {
    if (!input) {
      return null;
    }
    if (input === true) {
      return { showAngles: true };
    }
    return input;
  }

  private resolveQuadrantsConfig(input: boolean | QuadrantsConfig | undefined): QuadrantsConfig | null {
    if (!input) {
      return null;
    }
    if (input === true) {
      return { labels: true };
    }
    return input;
  }

  // ===========================================================================
  // ELEMENT CREATION HELPERS
  // ===========================================================================

  private createAxisMarkers(
    variant: 'coordinates' | 'radians' | 'degrees',
    radius: number,
    center: [number, number],
    elementFactory: ElementFactoryType,
  ): void {
    const data = getAxisMarkerData(variant);
    for (const { angle, label, position } of data) {
      const x = center[0] + radius * Math.cos(angle);
      const y = center[1] + radius * Math.sin(angle);

      elementFactory.point({
        coords: [x, y],
        name: label,
        style: {
          color: 'neutral-600',
          size: 3,
          fixed: true,
          label: { text: label, position, visible: true },
        },
      });
    }
  }

  private createTrigLines(
    trigLines: TrigLine[],
    radius: number,
    center: [number, number],
    display: UnitCircleWidgetConfig['display'],
    elementFactory: ElementFactoryType,
  ): void {
    for (const tl of trigLines) {
      const endpoints = computeTrigLineEndpoints(this.currentAngle, radius);
      const ep = endpoints[tl];
      const styleOverride = display?.trigLines?.[tl];
      const defaultColor = TRIG_LINE_COLORS[tl];
      const labelPos = getTrigLineLabelPosition(tl, this.currentAngle);

      const result = elementFactory.line({
        a: [center[0] + ep.from[0], center[1] + ep.from[1]],
        b: [center[0] + ep.to[0], center[1] + ep.to[1]],
        name: tl,
        style: {
          color: defaultColor,
          strokeWidth: 3,
          ...styleOverride,
          label: { text: `$\\${tl}$`, visible: true, position: labelPos, anchorAt: 'middle', spacing: 'compact' },
        },
      });
      this.trigLineElements.set(tl, {
        line: result.line,
        a: result.a,
        b: result.b,
        lineLabel: result.lineLabel,
      });
      this.objects.set(`trig-${tl}`, result.line);
    }
  }

  private createReferenceTriangle(
    radius: number,
    center: [number, number],
    display: UnitCircleWidgetConfig['display'],
    elementFactory: ElementFactoryType,
  ): void {
    const cosA = Math.cos(this.currentAngle);
    const sinA = Math.sin(this.currentAngle);

    const result = elementFactory.polygon({
      vertices: [
        center,
        [center[0] + radius * cosA, center[1]],
        [center[0] + radius * cosA, center[1] + radius * sinA],
      ],
      style: display?.referenceTriangle ?? {
        fillColor: 'sky-100',
        fillOpacity: 0.3,
        strokeColor: 'sky-300',
        strokeWidth: 1,
        vertex: { visible: false },
      },
      vertexNames: ['', '', ''],
    });
    this.refTriangleVertices = result.vertices;
    this.objects.set('refTriangle', result.polygon);
  }

  private createAxisProjections(
    radius: number,
    center: [number, number],
    display: UnitCircleWidgetConfig['display'],
    elementFactory: ElementFactoryType,
  ): void {
    const cosA = Math.cos(this.currentAngle);
    const sinA = Math.sin(this.currentAngle);
    const px = center[0] + radius * cosA;
    const py = center[1] + radius * sinA;
    const projStyle = display?.projections ?? { color: 'neutral-300', strokeWidth: 1, dash: 3 };

    const hResult = elementFactory.line({ a: [px, py], b: [center[0], py], style: projStyle });
    this.projectionElements.push(hResult.line, hResult.a, hResult.b);
    this.objects.set('proj-h', hResult.line);

    const vResult = elementFactory.line({ a: [px, py], b: [px, center[1]], style: projStyle });
    this.projectionElements.push(vResult.line, vResult.a, vResult.b);
    this.objects.set('proj-v', vResult.line);
  }

  private createValueLabels(valueLabels: ValueLabelConfig[], radius: number, center: [number, number]): void {
    const edgePaddingPx = 0.15;

    for (const vl of valueLabels) {
      const pos = vl.position ?? (vl.value === 'cos' ? 'canvasBottom' : 'canvasLeft');
      const isHorizontalEdge = pos === 'canvasBottom' || pos === 'canvasTop';
      const trigFn = vl.value === 'cos' ? Math.cos : Math.sin;
      const color = this.themeService.resolveColorToken(TRIG_LINE_COLORS[vl.value === 'cos' ? 'cos' : 'sin']);

      const getX = isHorizontalEdge
        ? () => center[0] + radius * Math.cos(this.currentAngle)
        : () => {
            const bb = this.board.getBoundingBox();
            return pos === 'canvasLeft' ? bb[0] + edgePaddingPx : bb[2] - edgePaddingPx;
          };

      const getY = isHorizontalEdge
        ? () => {
            const bb = this.board.getBoundingBox();
            return pos === 'canvasBottom' ? bb[3] + edgePaddingPx : bb[1] - edgePaddingPx;
          }
        : () => center[1] + radius * Math.sin(this.currentAngle);

      const getText = () => {
        const val = (radius * trigFn(this.currentAngle)).toFixed(2);
        return renderLatex(`$${val}$`);
      };

      const anchorX = isHorizontalEdge ? 'middle' : pos === 'canvasLeft' ? 'left' : 'right';
      const anchorY = isHorizontalEdge ? (pos === 'canvasBottom' ? 'bottom' : 'top') : 'middle';

      const text = this.board.create('text', [getX, getY, getText], {
        parse: false,
        anchorX,
        anchorY,
        fixed: true,
        highlight: false,
        cssStyle: `font-size: 11px; font-weight: 600; color: ${color};`,
      }) as GeometryElement;
      this.objects.set(`value-label-${vl.value}-${pos}`, text);
    }
  }

  private createSpecialAngles(
    radius: number,
    center: [number, number],
    saConfig: SpecialAnglesConfig,
    angleUnit: 'deg' | 'rad',
    display: UnitCircleWidgetConfig['display'],
    elementFactory: ElementFactoryType,
  ): void {
    const angles = getSpecialAngles();
    const showAngles = saConfig.showAngles !== false;
    const showCoords = saConfig.showCoords === true;
    const showTraces = saConfig.showTraces === true;
    const traceStyle = display?.projections ?? { color: 'neutral-200', strokeWidth: 1, dash: 3 };

    // Build highlight map for quick lookup
    const highlightMap = new Map<number, ThemeColorWithShade>();
    if (saConfig.highlights) {
      for (const h of saConfig.highlights) {
        const idx = findSpecialAngleIndex(h.angle);
        if (idx >= 0 && h.color) {
          highlightMap.set(idx, h.color);
        }
      }
    }

    for (let i = 0; i < angles.length; i++) {
      const a = angles[i];
      const x = center[0] + radius * Math.cos(a);
      const y = center[1] + radius * Math.sin(a);

      // Build label text — use space separator (no line break)
      const parts: string[] = [];
      if (showAngles) {
        parts.push(getSpecialAngleAngleLabel(i, angleUnit));
      }
      if (showCoords) {
        parts.push(getSpecialAngleCoordLabel(i));
      }
      const labelText = parts.join(' ');

      const highlightColor = highlightMap.get(i);
      const pointColor: ThemeColorWithShade = highlightColor ?? display?.specialAnglePoints?.color ?? 'neutral-600';
      const position = getSpecialAngleLabelPosition(i);

      const labelOverrides = display?.specialAngleLabels;
      const ptResult = elementFactory.point({
        coords: [x, y],
        name: labelText || undefined,
        style: {
          color: pointColor,
          size: display?.specialAnglePoints?.size ?? 2,
          fixed: true,
          ...(labelText
            ? {
                label: {
                  text: labelText,
                  position,
                  visible: true,
                  variant: labelOverrides?.variant ?? ('tertiary' as const),
                  ...(labelOverrides?.color ? { color: labelOverrides.color } : {}),
                  ...(labelOverrides?.fontSize ? { fontSize: labelOverrides.fontSize } : {}),
                },
              }
            : {}),
        },
      });
      this.objects.set(`special-pt-${i}`, ptResult.point);

      // Trace lines to axes
      if (showTraces) {
        if (i !== 0 && i !== 4 && i !== 8 && i !== 12) {
          elementFactory.line({ a: [x, y], b: [center[0], y], style: traceStyle });
          elementFactory.line({ a: [x, y], b: [x, center[1]], style: traceStyle });
        }
      }
    }
  }

  private createQuadrantInfo(
    radius: number,
    center: [number, number],
    config: QuadrantsConfig,
    hasPoint: boolean,
  ): void {
    const showLabels = config.labels !== false;
    const showSigns = config.signIndicators === true && hasPoint;

    if (!showLabels && !showSigns) {
      return;
    }

    const quadrantLabels = ['I', 'II', 'III', 'IV'];
    // Place each annotation at the centroid of the quadrant: 45° into each quadrant, at ~55% of the radius
    const quadrantAngles = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
    const placementRadius = radius * 0.55;
    const color = this.themeService.resolveColorToken('neutral-400');

    for (let i = 0; i < 4; i++) {
      const angle = quadrantAngles[i];
      const x = center[0] + placementRadius * Math.cos(angle);
      const y = center[1] + placementRadius * Math.sin(angle);

      // Build HTML content — combine label + signs into a single centered block
      const lines: string[] = [];
      if (showLabels) {
        lines.push(`<span style="font-size:16px;font-weight:600">${quadrantLabels[i]}</span>`);
      }
      if (showSigns) {
        const repAngle = (i * Math.PI) / 2 + Math.PI / 4;
        const signs = getSignInfo(repAngle);
        const katexHtml = renderLatex(`$\\sin ${signs.sin}\\;/\\;\\cos ${signs.cos}$`);
        lines.push(`<span style="font-size:10px">${katexHtml}</span>`);
      }

      const html = `<div style="display:flex;flex-direction:column;align-items:center;color:${color};user-select:none;line-height:1.4">${lines.join(
        '',
      )}</div>`;

      const text = this.board.create('text', [x, y, html], {
        parse: false,
        anchorX: 'middle',
        anchorY: 'middle',
        fixed: true,
        highlight: false,
      }) as GeometryElement;
      this.objects.set(`quadrant-${i}`, text);
    }
  }

  private createTraceWave(
    wave: 'sin' | 'cos' | 'tan',
    radius: number,
    center: [number, number],
    display: UnitCircleWidgetConfig['display'],
    elementFactory: ElementFactoryType,
  ): void {
    const waveOffset = center[0] + radius + 0.5;
    const curveStyle = display?.traceWaveCurve ?? {};
    const defaultColors: Record<string, ThemeColorWithShade> = { sin: 'sky-500', cos: 'red-500', tan: 'amber-500' };
    const color = curveStyle.color ?? defaultColors[wave] ?? 'sky-500';

    // Faint full-period reference curve
    const resolvedFaint = this.themeService.resolveColorToken(color.replace('-500', '-200') as ThemeColorWithShade);
    this.board.create(
      'curve',
      [
        (t: number) => waveOffset + t,
        (t: number) => {
          if (wave === 'tan') {
            const v = Math.tan(t);
            return Math.abs(v) > 10 ? NaN : center[1] + radius * v;
          }
          return center[1] + radius * (wave === 'sin' ? Math.sin(t) : Math.cos(t));
        },
        0,
        2 * Math.PI,
      ],
      { strokeColor: resolvedFaint, strokeWidth: 1, dash: 3, highlight: false, fixed: true },
    );

    // Active trace up to current angle
    const resolvedColor = this.themeService.resolveColorToken(color);
    this.traceWaveCurve = this.board.create(
      'curve',
      [
        (t: number) => waveOffset + t,
        (t: number) => {
          if (wave === 'tan') {
            const v = Math.tan(t);
            return Math.abs(v) > 10 ? NaN : center[1] + radius * v;
          }
          return center[1] + radius * (wave === 'sin' ? Math.sin(t) : Math.cos(t));
        },
        0,
        () => this.currentAngle,
      ],
      {
        strokeColor: resolvedColor,
        strokeWidth: curveStyle.strokeWidth ?? 2,
        highlight: false,
        fixed: true,
      },
    ) as GeometryElement;
    this.objects.set('traceWave', this.traceWaveCurve);

    // Connecting dashed line from circle point to wave
    const connColor = this.themeService.resolveColorToken('neutral-300');
    this.board.create(
      'curve',
      [
        (t: number) => {
          const px = center[0] + radius * Math.cos(this.currentAngle);
          return px + t * (waveOffset + this.currentAngle - px);
        },
        (t: number) => {
          const py = center[1] + radius * Math.sin(this.currentAngle);
          const waveY =
            wave === 'tan'
              ? center[1] + radius * Math.tan(this.currentAngle)
              : center[1] + radius * (wave === 'sin' ? Math.sin(this.currentAngle) : Math.cos(this.currentAngle));
          return py + t * (waveY - py);
        },
        0,
        1,
      ],
      { strokeColor: connColor, strokeWidth: 1, dash: 3, highlight: false, fixed: true },
    );

    // Wave endpoint dot
    elementFactory.point({
      coords: [
        () => waveOffset + this.currentAngle,
        () => {
          if (wave === 'tan') {
            const v = Math.tan(this.currentAngle);
            return Math.abs(v) > 10 ? NaN : center[1] + radius * v;
          }
          return center[1] + radius * (wave === 'sin' ? Math.sin(this.currentAngle) : Math.cos(this.currentAngle));
        },
      ],
      style: { color, size: 4, fixed: true },
    });
  }

  // ===========================================================================
  // DYNAMIC UPDATE (called on drag)
  // ===========================================================================

  private updateDynamicElements(): void {
    if (!this.board) {
      return;
    }

    const params = this.config?.params;
    if (!params) {
      return;
    }

    const radius = params.radius ?? 1;
    const center: [number, number] = params.center ?? [0, 0];

    // Update trig lines + auto-position labels
    if (this.trigLineElements.size > 0) {
      const endpoints = computeTrigLineEndpoints(this.currentAngle, radius);
      for (const [tl, elems] of this.trigLineElements) {
        const ep = endpoints[tl];
        const a = elems.a as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void };
        const b = elems.b as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void };
        a.setPositionDirectly(1, [center[0] + ep.from[0], center[1] + ep.from[1]]);
        b.setPositionDirectly(1, [center[0] + ep.to[0], center[1] + ep.to[1]]);

        // Auto-switch label position based on quadrant
        if (elems.lineLabel) {
          const newPos = getTrigLineLabelPosition(tl, this.currentAngle);
          elems.lineLabel.reconfigure({ position: newPos });
        }
      }
    }

    // Update reference triangle
    if (this.refTriangleVertices.length === 3) {
      const cosA = Math.cos(this.currentAngle);
      const sinA = Math.sin(this.currentAngle);
      const setPos = (el: GeometryElement, coords: [number, number]) => {
        (el as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void }).setPositionDirectly(
          1,
          coords,
        );
      };
      setPos(this.refTriangleVertices[0], center);
      setPos(this.refTriangleVertices[1], [center[0] + radius * cosA, center[1]]);
      setPos(this.refTriangleVertices[2], [center[0] + radius * cosA, center[1] + radius * sinA]);
    }

    // Update projections
    if (this.projectionElements.length >= 6) {
      const cosA = Math.cos(this.currentAngle);
      const sinA = Math.sin(this.currentAngle);
      const px = center[0] + radius * cosA;
      const py = center[1] + radius * sinA;
      const setPos = (el: GeometryElement, coords: [number, number]) => {
        (el as unknown as { setPositionDirectly: (m: number, c: [number, number]) => void }).setPositionDirectly(
          1,
          coords,
        );
      };
      setPos(this.projectionElements[1], [px, py]);
      setPos(this.projectionElements[2], [center[0], py]);
      setPos(this.projectionElements[4], [px, py]);
      setPos(this.projectionElements[5], [px, center[1]]);
    }

    // Update coordinate label
    if (this.coordLabel) {
      const text = this.getCoordLabelText(params.radius ?? 1);
      this.coordLabel.reconfigure({ text });
    }

    this.board.update();
  }

  // ===========================================================================
  // UTILITY HELPERS
  // ===========================================================================

  private getCoordLabelText(radius: number): string {
    const cosVal = (radius * Math.cos(this.currentAngle)).toFixed(2);
    const sinVal = (radius * Math.sin(this.currentAngle)).toFixed(2);
    return `$(${cosVal},\\, ${sinVal})$`;
  }

  private buildDefaultAxes(
    traceWave: 'sin' | 'cos' | 'tan' | undefined,
    angleUnit: 'deg' | 'rad',
  ): Array<{
    id: string;
    orientation: 'horizontal' | 'vertical';
    position: number;
    isPrimary: boolean;
    ticks?: Record<string, unknown>;
  }> {
    if (traceWave) {
      return [
        {
          id: 'x',
          orientation: 'horizontal',
          position: 0,
          isPrimary: true,
          ticks: {
            ticksDistance: Math.PI / 2,
            labelFormat: angleUnit === 'deg' ? 'degrees' : 'pi',
          },
        },
        { id: 'y', orientation: 'vertical', position: 0, isPrimary: true, ticks: { ticksDistance: 1 } },
      ];
    }
    return [
      { id: 'x', orientation: 'horizontal', position: 0, isPrimary: true, ticks: { ticksDistance: 1 } },
      { id: 'y', orientation: 'vertical', position: 0, isPrimary: true, ticks: { ticksDistance: 1 } },
    ];
  }
}
